import { useState } from 'react';

import { useIntl } from 'react-intl';
import { useUpdateEffect } from 'react-use';
import { isEqual } from 'lodash';

import { FiltersPanel } from 'components/UI';
import { Dropdown } from 'components/Forms';
import { DeviceLocations } from 'constants/enums';
import { getDeviceConditionOptions, getDeviceStatusOptions } from 'constants/constants';
import useAppSelector from 'hooks/useAppSelector';
import { getCurrentGroup, getCurrentOrgSubgroups, getDeviceOrderings } from 'store/groups/reducer';
import { formatDropdownOptions } from 'utils/Object';
import { isOrganization } from 'utils/Groups';

import Subgroups from '../Filters/Subgroups';

interface IDeviceFilters {
  filters: IDeviceTemplateFilters[];
  setFilters: (filters: IDeviceTemplateFilters[]) => void;
}

const DeviceFilters = ({ filters, setFilters }: IDeviceFilters) => {
  const { formatMessage } = useIntl();
  const deviceOrderings = useAppSelector(getDeviceOrderings);
  const currentGroup = useAppSelector(getCurrentGroup);
  const subgroups = useAppSelector(getCurrentOrgSubgroups) || [];
  const subgroupsFilterOptions =
    currentGroup && currentGroup.gid !== 'all' && isOrganization(currentGroup) ? subgroups : [];
  const [selectedFilters, setSelectedFilters] = useState<IDeviceTemplateFilters[]>([...filters]);

  const deviceLocations: ISelectOption[] = [
    {
      label: formatMessage({ id: 'officeKey' }),
      value: DeviceLocations.OFFICE
    },
    {
      label: formatMessage({ id: 'patientHomeKey' }),
      value: DeviceLocations.PATIENT_HOME
    }
  ];

  const batteryLevelOptions: ISelectOption[] = [
    {
      label: formatMessage({ id: 'normalKey' }),
      value: 'normal'
    },
    {
      label: formatMessage({ id: 'lowKey' }),
      value: 'low'
    },
    {
      label: formatMessage({ id: 'notReceivedKey' }),
      value: 'none'
    }
  ];

  const createDeviceTypeOptions = () => {
    const uniqueDescriptions = [
      ...new Set(deviceOrderings.map((ordering) => ordering.displayType))
    ].filter((description) => description && description !== null);
    return uniqueDescriptions.map((description) => ({
      label: formatMessage({ id: description }),
      value: description
    }));
  };

  const createDeviceModelOptions = () => {
    const uniqueNames = [
      ...new Set(deviceOrderings.map((ordering) => ordering.displayName))
    ].filter((displayName) => displayName && displayName !== null);
    return uniqueNames.map((displayName) => ({
      label: formatMessage({ id: displayName }),
      value: displayName
    }));
  };

  const handleApply = () => {
    setFilters(selectedFilters);
  };

  const clearSelected = () => {
    setFilters([]);
    setSelectedFilters([]);
  };

  const handleChange = (type: IDeviceFilterLabel, data?: ISelectOption[]) => {
    const oldFilters: IDeviceTemplateFilters[] = [...selectedFilters].filter(
      (filter) => filter.filterLabel !== type
    );
    if (data && data.length === 0) {
      setSelectedFilters(selectedFilters.filter((filter) => filter.filterLabel !== type));
    } else {
      const newTypeFilters: IDeviceTemplateFilters = {
        filterLabel: type,
        ...(data && { filterData: data, filterValue: data?.map((x) => x.value) })
      };
      const newFilters: IDeviceTemplateFilters[] = [...oldFilters];
      if (data) {
        newFilters.push(newTypeFilters);
      }
      setSelectedFilters(newFilters);
    }
  };

  const getFiltersDataByLabel = (data: IDeviceTemplateFilters[], label: IDeviceFilterLabel) => {
    return data.find((filter) => filter.filterLabel === label)?.filterData || [];
  };

  useUpdateEffect(() => {
    setSelectedFilters(filters);
  }, [filters]);

  return (
    <FiltersPanel
      onApply={handleApply}
      onClear={clearSelected}
      disableApply={isEqual(selectedFilters, filters)}
      onClose={() => {
        if (selectedFilters !== filters) setSelectedFilters(filters);
      }}
      clearable={!!selectedFilters.length}
      hasMargins={false}
      filtersCount={selectedFilters.length}
    >
      <>
        {subgroupsFilterOptions.length > 1 && (
          <>
            <Subgroups
              options={formatDropdownOptions(subgroupsFilterOptions, 'gid', 'name')}
              value={getFiltersDataByLabel(selectedFilters, 'subgroups')}
              handleChange={(data: ISelectOption[]) => handleChange('subgroups', data)}
            />
            <hr />
          </>
        )}
        <Dropdown
          variant="form"
          options={deviceLocations}
          onChange={(data: ISelectOption[]) => handleChange('locations', data)}
          isMulti
          value={getFiltersDataByLabel(selectedFilters, 'locations')}
          label={formatMessage({ id: 'locationKey' })}
        />
        <hr />
        <Dropdown
          variant="form"
          options={getDeviceConditionOptions()}
          onChange={(data: ISelectOption[]) => handleChange('conditions', data)}
          isMulti
          value={getFiltersDataByLabel(selectedFilters, 'conditions')}
          label={formatMessage({ id: 'conditionKey' })}
        />
        <hr />
        <Dropdown
          variant="form"
          options={getDeviceStatusOptions()}
          onChange={(data: ISelectOption[]) => handleChange('deviceStatus', data)}
          isMulti
          value={getFiltersDataByLabel(selectedFilters, 'deviceStatus')}
          label={formatMessage({ id: 'statusKey' })}
        />
        <hr />
        <Dropdown
          variant="form"
          options={createDeviceModelOptions()}
          onChange={(data: ISelectOption[]) => handleChange('models', data)}
          isMulti
          value={getFiltersDataByLabel(selectedFilters, 'models')}
          label={formatMessage({ id: 'modelKey' })}
          menuPosition="fixed"
        />
        <hr />
        <Dropdown
          variant="form"
          options={createDeviceTypeOptions()}
          onChange={(data: ISelectOption[]) => handleChange('types', data)}
          isMulti
          value={getFiltersDataByLabel(selectedFilters, 'types')}
          label={formatMessage({ id: 'typeKey' })}
        />
        <hr />
        <Dropdown
          variant="form"
          options={batteryLevelOptions}
          onChange={(data: ISelectOption) => handleChange('batteryLevel', data ? [data] : [])}
          value={getFiltersDataByLabel(selectedFilters, 'batteryLevel')[0] ?? null}
          label={formatMessage({ id: 'batteryLevelKey' })}
        />
      </>
    </FiltersPanel>
  );
};

export default DeviceFilters;
